// VideoShowcaseSection.js — Apple-style cinematic product demo
// Real dubbed videos per language. Switching keeps the same timestamp
// so users can compare the dub at any moment of the clip.
import { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { assetUrl } from '../../utils/assets';

// Videos resolve to S3 when REACT_APP_ASSETS_URL is set; fall back to
// local /demo paths in dev. Each entry carries its own `origLang` — the
// three Indian demos at the top are dubbed from different source clips
// (English or Gujarati), not from the shared "original.mp4" reel.
const LANGUAGES = [
  // Real customer demos — different source clips per entry.
  { code: 'hi-real', label: 'Hindi',   native: 'Hindi',   origLang: 'English',  src: assetUrl('videoHindi.mp4', '/demo/hindi.mp4'),    badge: 'Dubbed · Hindi (from English)',    sub: '"एक ही आवाज़। हर भाषा।"',      dot: '#38bdf8' },
  { code: 'kn-real', label: 'Kannada', native: 'Kannada', origLang: 'English',  src: assetUrl('kannadaDemo.mp4', '/demo/kannada.mp4'), badge: 'Dubbed · Kannada (from English)',  sub: '"ಅದೇ ಧ್ವನಿ. ಪ್ರತಿ ಭಾಷೆ."',    dot: '#38bdf8' },
  { code: 'mr-real', label: 'Marathi', native: 'Marathi', origLang: 'Gujarati', src: assetUrl('marathi.mov', '/demo/marathi.mov'),     badge: 'Dubbed · Marathi (from Gujarati)', sub: '"तोच आवाज. प्रत्येक भाषा."',   dot: '#38bdf8' },

  // Shared-source reel — same "original.mp4" clip dubbed into 5 languages.
  { code: 'en', label: 'English',  native: 'Original', origLang: 'English', src: assetUrl('original.mp4', '/demo/original.mp4'), badge: 'Original · English', sub: '"Same voice. Every language."',               dot: '#facc15' },
  { code: 'es', label: 'Español',  native: 'Spanish',  origLang: 'English', src: assetUrl('spanish.mp4',  '/demo/spanish.mp4'),  badge: 'Dubbed · Spanish',   sub: '"Misma voz. Cada idioma."',                   dot: '#4ade80' },
  { code: 'fr', label: 'Français', native: 'French',   origLang: 'English', src: assetUrl('french.mp4',   '/demo/french.mp4'),   badge: 'Dubbed · French',    sub: '"Même voix. Chaque langue."',                 dot: '#4ade80' },
  { code: 'zh', label: 'Mandarin', native: 'Mandarin', origLang: 'English', src: '/demo/mandarin.mp4',                            badge: 'Dubbed · Mandarin',  sub: '"同一个声音，每一种语言。"',                      dot: '#4ade80' },
  { code: 'de', label: 'Deutsch',  native: 'German',   origLang: 'English', src: assetUrl('german.mp4',   '/demo/german.mp4'),   badge: 'Dubbed · German',    sub: '"Gleiche Stimme. Jede Sprache."',             dot: '#4ade80' },
];

const MARKERS = [
  { frac: 0.22, label: 'Voice Cloned' },
  { frac: 0.55, label: 'Lip Synced'   },
  { frac: 0.81, label: 'Translated'   },
];

function fmt(s) {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
}

export default function VideoShowcaseSection() {
  const outerRef    = useRef(null);
  const videoRef    = useRef(null);
  const rafRef      = useRef(null);
  const savedTime   = useRef(0);
  const wasPlaying  = useRef(false);
  const isFirstRun  = useRef(true);

  const [lang, setLang]           = useState('hi-real');
  const [flashing, setFlashing]   = useState(false);
  const [playing, setPlaying]     = useState(false);
  const [muted, setMuted]         = useState(true);
  const [vidProg, setVidProg]     = useState(0);
  const [duration, setDuration]   = useState(1);
  const [curTime, setCurTime]     = useState(0);
  const [showSplit, setShowSplit] = useState(false);

  const { scrollYProgress } = useScroll({ target: outerRef, offset: ['start start', 'end end'] });

  const headlineOp   = useTransform(scrollYProgress, [0, 0.14], [0, 1]);
  const headlineY    = useTransform(scrollYProgress, [0, 0.14], [36, 0]);
  const macbookOp    = useTransform(scrollYProgress, [0.08, 0.26], [0, 1]);
  const macbookScale = useTransform(scrollYProgress, [0.08, 0.26], [0.87, 1]);
  const tiltX        = useTransform(scrollYProgress, [0.24, 0.52], [5, 0]);
  const tiltY        = useTransform(scrollYProgress, [0.24, 0.52], [-9, 0]);

  useEffect(() => scrollYProgress.on('change', v => setShowSplit(v > 0.60)), [scrollYProgress]);

  // Track video time via RAF
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onMeta = () => setDuration(video.duration || 1);
    video.addEventListener('loadedmetadata', onMeta);
    const tick = () => {
      if (video.duration) {
        setCurTime(video.currentTime);
        setVidProg(video.currentTime / video.duration);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      video.removeEventListener('loadedmetadata', onMeta);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Swap video src when language changes, restoring the same timestamp
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Skip imperative switch on first render — JSX src handles initial load
    if (isFirstRun.current) { isFirstRun.current = false; return; }

    const newSrc = LANGUAGES.find(l => l.code === lang)?.src;
    if (!newSrc) return;

    video.src = newSrc;
    video.load();

    const onCanPlay = () => {
      video.currentTime = savedTime.current;
      video.muted = muted;
      if (wasPlaying.current) video.play().catch(() => {});
    };
    video.addEventListener('canplay', onCanPlay, { once: true });
    return () => video.removeEventListener('canplay', onCanPlay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }, []);

  const seek = useCallback((e) => {
    const v = videoRef.current;
    if (!v?.duration) return;
    const r = e.currentTarget.getBoundingClientRect();
    v.currentTime = ((e.clientX - r.left) / r.width) * v.duration;
  }, []);

  const switchLang = useCallback((code) => {
    if (code === lang) return;
    const v = videoRef.current;
    if (v) {
      savedTime.current  = v.currentTime;
      wasPlaying.current = !v.paused;
      v.pause();
    }
    setPlaying(false);
    setFlashing(true);
    setLang(code);
    setTimeout(() => setFlashing(false), 320);
  }, [lang]);

  const active = LANGUAGES.find(l => l.code === lang);

  return (
    <Box ref={outerRef} sx={{ height: '180vh', position: 'relative' }}>
      <Box sx={{
        position: 'sticky', top: 0, height: '100vh',
        background: '#000', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        px: 2, py: { xs: 3, md: 4 },
      }}>

        {/* Ambient glow */}
        <Box sx={{
          position: 'absolute', top: '-20%', left: '50%', transform: 'translateX(-50%)',
          width: '70vw', height: '70vw', maxWidth: 700, maxHeight: 700,
          borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(255,255,255,0.028) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }} />

        {/* ── Headline ── */}
        <motion.div style={{ opacity: headlineOp, y: headlineY, textAlign: 'center', marginBottom: 20, zIndex: 2, position: 'relative' }}>
          <Typography sx={{
            fontSize: '9px', fontWeight: 700, letterSpacing: '0.3em',
            color: 'rgba(255,255,255,0.22)', fontFamily: 'Inter',
            textTransform: 'uppercase', mb: 1.5,
          }}>
            02 — Real Product Demo
          </Typography>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 900,
            fontSize: { xs: '1.55rem', sm: '2rem', md: '2.6rem' },
            color: '#fff', letterSpacing: '-0.045em', lineHeight: 1.05,
          }}>
            This is not a demo.
          </Typography>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 900,
            fontSize: { xs: '1.55rem', sm: '2rem', md: '2.6rem' },
            color: 'rgba(255,255,255,0.18)', letterSpacing: '-0.045em', lineHeight: 1.05, mb: 1.2,
          }}>
            This is the real product.
          </Typography>
          <Typography sx={{
            fontSize: { xs: '11px', md: '12px' }, color: 'rgba(255,255,255,0.28)',
            fontFamily: 'Inter', fontWeight: 400,
          }}>
            Real dubs across eight languages. Click any language — the dub plays instantly.
          </Typography>
        </motion.div>

        {/* ── MacBook ── */}
        <motion.div style={{
          opacity: macbookOp, scale: macbookScale,
          rotateX: tiltX, rotateY: tiltY,
          perspective: 1200,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          width: '100%', position: 'relative', zIndex: 2,
        }}>
          <Box sx={{
            width: { xs: '90vw', sm: '72vw', md: '57vw', lg: '50vw' },
            maxWidth: 800,
          }}>
            {/* Screen bezel */}
            <Box sx={{
              background: 'linear-gradient(150deg, #2d2d2d 0%, #1b1b1b 100%)',
              borderRadius: '10px 10px 0 0',
              border: '2px solid #3c3c3c',
              p: { xs: '9px 9px 6px', md: '13px 13px 9px' },
              boxShadow: '0 0 0 1px #0a0a0a, 0 28px 56px rgba(0,0,0,0.8)',
            }}>
              {/* Camera dot */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.8 }}>
                <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: '#3d3d3d' }} />
              </Box>

              {/* ── Video screen ── */}
              <Box sx={{ position: 'relative', aspectRatio: '16/9', background: '#111', borderRadius: '5px', overflow: 'hidden' }}>
                <video
                  ref={videoRef}
                  src={assetUrl('videoHindi.mp4', '/demo/hindi.mp4')}
                  muted
                  loop
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                  onClick={togglePlay}
                />

                {/* Flash on language switch */}
                <AnimatePresence>
                  {flashing && (
                    <motion.div
                      initial={{ opacity: 0 }} exit={{ opacity: 0 }}
                      animate={{ opacity: [0, 0.5, 0] }}
                      transition={{ duration: 0.32 }}
                      style={{ position: 'absolute', inset: 0, zIndex: 12, background: '#fff', pointerEvents: 'none' }}
                    />
                  )}
                </AnimatePresence>

                {/* Language badge */}
                <AnimatePresence mode="wait">
                  <motion.div key={lang + '-badge'}
                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }} transition={{ duration: 0.22 }}
                    style={{ position: 'absolute', top: 10, left: 10, zIndex: 8 }}
                  >
                    <Box sx={{
                      background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.14)', borderRadius: '4px',
                      px: 1, py: 0.4, display: 'inline-flex', alignItems: 'center', gap: 0.7,
                    }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: active.dot, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '8px', color: '#fff', fontFamily: 'Inter', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        {active.badge}
                      </Typography>
                    </Box>
                  </motion.div>
                </AnimatePresence>

                {/* DubLabs watermark */}
                <Box sx={{
                  position: 'absolute', top: 10, right: 10, zIndex: 8,
                  background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
                  border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', px: 1, py: 0.4,
                }}>
                  <Typography sx={{ fontSize: '7px', color: 'rgba(255,255,255,0.45)', fontFamily: 'Inter', fontWeight: 700, letterSpacing: '0.14em' }}>
                    DUBLABS AI
                  </Typography>
                </Box>

                {/* Subtitle */}
                <AnimatePresence mode="wait">
                  <motion.div key={lang + '-sub'}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22 }}
                    style={{ position: 'absolute', bottom: 36, left: 0, right: 0, textAlign: 'center', padding: '0 14px', zIndex: 8 }}
                  >
                    <Typography sx={{
                      display: 'inline-block',
                      background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)',
                      px: 1.5, py: 0.5, borderRadius: '3px',
                      fontSize: { xs: '8px', md: '10px' }, color: '#fff',
                      fontFamily: 'Inter', fontWeight: 400, fontStyle: 'italic',
                    }}>
                      {active.sub}
                    </Typography>
                  </motion.div>
                </AnimatePresence>

                {/* Play overlay */}
                <AnimatePresence>
                  {!playing && (
                    <motion.div
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      onClick={togglePlay}
                      style={{
                        position: 'absolute', inset: 0, zIndex: 5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,0.22)', cursor: 'pointer',
                      }}
                    >
                      <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.92 }}>
                        <Box sx={{
                          width: 50, height: 50, borderRadius: '50%',
                          background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(14px)',
                          border: '1.5px solid rgba(255,255,255,0.38)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Box sx={{ width: 0, height: 0, ml: '3px', borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '15px solid #fff' }} />
                        </Box>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>

              {/* ── Player controls ── */}
              <Box sx={{ mt: 1.2, px: 0.5 }}>
                {/* Progress bar */}
                <Box
                  onClick={seek}
                  sx={{
                    position: 'relative', height: 3, borderRadius: 2,
                    background: 'rgba(255,255,255,0.1)', cursor: 'pointer', mb: 0.9,
                    '&:hover': { height: 4 }, transition: 'height 0.12s',
                  }}
                >
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${vidProg * 100}%`, background: '#fff', borderRadius: 2, transition: 'width 0.1s linear' }} />
                  {MARKERS.map(m => (
                    <Box key={m.label} sx={{
                      position: 'absolute', left: `${m.frac * 100}%`, top: '50%',
                      transform: 'translate(-50%,-50%)',
                      width: 7, height: 7, borderRadius: '50%', zIndex: 2,
                      background: vidProg >= m.frac ? '#fff' : '#444',
                      border: '1.5px solid rgba(0,0,0,0.6)', transition: 'background 0.35s',
                    }}>
                      <Box sx={{
                        position: 'absolute', bottom: '140%', left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0,0,0,0.85)', px: 0.9, py: 0.3,
                        borderRadius: '3px', whiteSpace: 'nowrap', pointerEvents: 'none',
                        opacity: (vidProg >= m.frac - 0.04 && vidProg <= m.frac + 0.12) ? 1 : 0,
                        transition: 'opacity 0.3s',
                      }}>
                        <Typography sx={{ fontSize: '6.5px', color: '#fff', fontFamily: 'Inter', fontWeight: 700, letterSpacing: '0.1em' }}>
                          {m.label.toUpperCase()}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Controls row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  {/* Play/Pause */}
                  <Box onClick={togglePlay} sx={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    border: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', '&:hover': { background: 'rgba(255,255,255,0.08)' },
                  }}>
                    {playing
                      ? <Box sx={{ display: 'flex', gap: '1.5px' }}><Box sx={{ width: 2, height: 7, background: '#fff', borderRadius: 1 }} /><Box sx={{ width: 2, height: 7, background: '#fff', borderRadius: 1 }} /></Box>
                      : <Box sx={{ width: 0, height: 0, ml: '1px', borderTop: '4px solid transparent', borderBottom: '4px solid transparent', borderLeft: '7px solid #fff' }} />
                    }
                  </Box>

                  {/* Mute toggle */}
                  <Box onClick={toggleMute} title={muted ? 'Unmute to hear the dubbed voice' : 'Mute'} sx={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    border: `1px solid ${muted ? 'rgba(255,200,0,0.4)' : 'rgba(255,255,255,0.2)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', '&:hover': { background: 'rgba(255,255,255,0.08)' },
                  }}>
                    <Typography sx={{ fontSize: '8px', lineHeight: 1 }}>{muted ? '🔇' : '🔊'}</Typography>
                  </Box>

                  {/* Time */}
                  <Typography sx={{ fontSize: '7.5px', color: 'rgba(255,255,255,0.28)', fontFamily: 'Inter', fontWeight: 500, letterSpacing: '0.06em', flexShrink: 0 }}>
                    {fmt(curTime)} / {fmt(duration)}
                  </Typography>

                  {/* Title */}
                  <Typography sx={{ fontSize: '7.5px', color: 'rgba(255,255,255,0.16)', fontFamily: 'Inter', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', flexGrow: 1, textAlign: 'center' }}>
                    DubLabs Demo
                  </Typography>

                  {/* Mini waveform */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 10 }}>
                    {Array.from({ length: 10 }).map((_, i) => (
                      <Box key={i} component={motion.div}
                        animate={playing ? { scaleY: [0.25, 1, 0.45, 0.85, 0.25] } : { scaleY: 0.2 }}
                        transition={{ duration: 0.7 + i * 0.07, repeat: Infinity, delay: i * 0.05 }}
                        sx={{ width: 1.5, borderRadius: 1, transformOrigin: 'center', background: 'rgba(255,255,255,0.2)', height: `${Math.abs(Math.sin(i * 0.6)) * 8 + 3}px` }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Unmute hint — shown when muted */}
                <AnimatePresence>
                  {muted && playing && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                    >
                      <Typography sx={{
                        fontSize: '7px', color: 'rgba(255,200,0,0.7)', fontFamily: 'Inter',
                        fontWeight: 600, letterSpacing: '0.1em', textAlign: 'center', mt: 0.5,
                      }}>
                        Click 🔇 to hear the dubbed voice
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </Box>

            {/* MacBook base */}
            <Box sx={{
              height: 7, width: '108%', mx: '-4%',
              background: 'linear-gradient(to bottom, #2d2d2d, #1e1e1e)',
              borderRadius: '0 0 3px 3px', boxShadow: '0 6px 30px rgba(0,0,0,0.65)',
            }} />
          </Box>

          {/* ── Language switcher ── */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2.2, flexWrap: 'wrap', justifyContent: 'center', px: 2 }}>
            {LANGUAGES.map((l, i) => (
              <motion.div key={l.code} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.06 }}>
                <Box
                  component={motion.div}
                  whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }}
                  onClick={() => switchLang(l.code)}
                  sx={{
                    px: 1.8, py: 0.6, borderRadius: '100px', cursor: 'pointer',
                    border: lang === l.code ? `1px solid ${l.dot}55` : '1px solid rgba(255,255,255,0.1)',
                    background: lang === l.code ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.02)',
                    transition: 'all 0.2s ease',
                    display: 'flex', alignItems: 'center', gap: 0.7,
                  }}
                >
                  {lang === l.code && (
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: l.dot, flexShrink: 0 }} />
                  )}
                  <Typography sx={{
                    fontSize: '11px', fontFamily: 'Inter', letterSpacing: '0.03em',
                    fontWeight: lang === l.code ? 700 : 400,
                    color: lang === l.code ? '#fff' : 'rgba(255,255,255,0.38)',
                    transition: 'color 0.2s',
                  }}>
                    {l.label}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>

          {/* ── Comparison row (appears on scroll) ── */}
          <AnimatePresence>
            {showSplit && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 16 }}
              >
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '8px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.22)', fontFamily: 'Inter', fontWeight: 700, textTransform: 'uppercase' }}>
                    Original
                  </Typography>
                  <AnimatePresence mode="wait">
                    <motion.div key={active.origLang}
                      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.22 }}
                    >
                      <Typography sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter', fontWeight: 700, mt: 0.3 }}>
                        {active.origLang}
                      </Typography>
                    </motion.div>
                  </AnimatePresence>
                </Box>
                <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} component={motion.div}
                      animate={{ opacity: [0.18, 1, 0.18] }}
                      transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.3 }}
                      sx={{ width: 3, height: 3, borderRadius: '50%', background: '#fff' }}
                    />
                  ))}
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ fontSize: '8px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.22)', fontFamily: 'Inter', fontWeight: 700, textTransform: 'uppercase' }}>
                    Dubbed by DubLabs
                  </Typography>
                  <AnimatePresence mode="wait">
                    <motion.div key={lang}
                      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.22 }}
                    >
                      <Typography sx={{ fontSize: '14px', color: lang === 'en' ? 'rgba(255,255,255,0.25)' : '#fff', fontFamily: 'Inter', fontWeight: 700, mt: 0.3 }}>
                        {lang === 'en' ? 'Select a language above ↑' : active.native}
                      </Typography>
                    </motion.div>
                  </AnimatePresence>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </Box>
    </Box>
  );
}
